import graphql from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import { isAvailableForBookingDuration } from "@/utils/slotAvailability";

import {
  SlotAvailability,
  useGetCourseOptionsFromValidCustomerBookingSelections_TimeSlot$data,
  useGetCourseOptionsFromValidCustomerBookingSelections_TimeSlot$key,
} from "./__generated__/useGetCourseOptionsFromValidCustomerBookingSelections_TimeSlot.graphql";

type CourseOption = {
  label: string | null | undefined;
  description: string | null | undefined;
  value: string | null | undefined;
  isDisabled: boolean;
  startSlotId: string | null | undefined;
  courseBucketId: string | null | undefined;
  time?: string;
};

type TimeSlotData =
  useGetCourseOptionsFromValidCustomerBookingSelections_TimeSlot$data;
type CourseData = NonNullable<TimeSlotData["course"]>;
type CourseOptionsInput = Pick<TimeSlotData, "id" | "endSlotOptions"> & {
  course: Pick<CourseData, "id" | "name" | "description" | "courseBucket">;
};
type EndSlotOptionEdge = NonNullable<
  NonNullable<TimeSlotData["endSlotOptions"]>["edges"]
>[number];
type SlotDuration = NonNullable<
  NonNullable<
    NonNullable<TimeSlotData["course"]>["courseBucket"]
  >["slotDurations"]
>[number];

const isAvailableForBooking = (availability: SlotAvailability) =>
  isAvailableForBookingDuration(availability);

const getValidDurationsForGroupSize = ({
  edges,
  currentGroupSize,
}: {
  edges: ReadonlyArray<EndSlotOptionEdge> | null | undefined;
  currentGroupSize: number;
}): Array<number | null | undefined> | undefined =>
  edges
    ?.filter((edge) =>
      isAvailableForBooking(edge?.node?.availability ?? "UNAVAILABLE")
    )
    .filter((edge) => edge?.allowedPlayerCounts?.includes(currentGroupSize))
    .map((edge) => edge?.durationMins);

export const getCourseOptionsFromValidCustomerBookingSelections = ({
  data,
  currentDuration,
  currentGroupSize,
}: {
  data: CourseOptionsInput | null | undefined;
  currentDuration: number;
  currentGroupSize: number;
}): CourseOption[] => {
  const validDurationsForInitialCourse = getValidDurationsForGroupSize({
    edges: data?.endSlotOptions?.edges,
    currentGroupSize,
  });
  const isInitialCourseDisabled =
    !validDurationsForInitialCourse?.includes(currentDuration);

  const initialCourseOption = data?.course
    ? {
        label: data.course.name,
        description: data.course.description,
        value: data.course.id,
        isDisabled: isInitialCourseDisabled,
        startSlotId: data.id,
        courseBucketId: data.course.courseBucket?.id,
      }
    : null;
  const courseOptions = [...(data?.course?.courseBucket?.slotDurations ?? [])];

  const optionsToSelect: CourseOption[] = courseOptions.map(
    (selection: SlotDuration) => {
      /**
       * We make sure a course selection can accomodate our duration/playercount combination
       */
      const validDurationsForCourse = getValidDurationsForGroupSize({
        edges: selection?.startSlot?.endSlotOptions?.edges,
        currentGroupSize,
      });
      const isDisabled = !validDurationsForCourse?.includes(currentDuration);

      return {
        label: selection?.startSlot?.course?.name,
        description: selection?.startSlot?.course?.description,
        value: selection?.startSlot?.course?.id,
        isDisabled,
        startSlotId: selection?.startSlot?.id,
        courseBucketId: data?.course?.courseBucket?.id,
        time: selection?.startSlot?.time,
      };
    }
  );

  const combinedOptions = initialCourseOption
    ? [initialCourseOption, ...optionsToSelect]
    : optionsToSelect;

  // De-duplicate by course value (course id)
  const dedupedOptions = Array.from(
    new Map(
      (combinedOptions ?? []).map((option) => [option?.value ?? "", option])
    ).values()
  );

  return dedupedOptions.sort((a, b) =>
    (a?.label || "") > (b?.label || "") ? 1 : -1
  );
};

export const useGetCourseOptionsFromValidCustomerBookingSelections = (
  ref: useGetCourseOptionsFromValidCustomerBookingSelections_TimeSlot$key | null,
  currentDuration: number,
  currentGroupSize: number
) => {
  const data = useFragment(
    useGetCourseOptionsFromValidCustomerBookingSelectionsFragmentString,
    ref
  );

  return useMemo(
    () =>
      getCourseOptionsFromValidCustomerBookingSelections({
        data,
        currentDuration,
        currentGroupSize,
      }),
    [data, currentDuration, currentGroupSize]
  );
};

export const useGetCourseOptionsFromValidCustomerBookingSelectionsFragmentString = graphql`
  fragment useGetCourseOptionsFromValidCustomerBookingSelections_TimeSlot on TimeSlot {
    ... on TimeSlot {
      id
      endSlotOptions(
        bookingId: $bookingId
        bookingType: $bookingType
        memberId: $memberId
      ) {
        edges {
          durationMins
          allowedPlayerCounts
          node {
            availability
          }
        }
      }

      course {
        id
        name
        description
        facility {
          publicBookingPermissionSet {
            customerCanSelectCourt
          }
          copyOverrides {
            course {
              singular
            }
          }
        }
        courseBucket {
          id
          slotDurations(agendaId: $agendaId, startsAt: $startTime) {
            startSlot {
              id
              time
              course {
                id
                name
                description
              }
              endSlotOptions(
                bookingId: $bookingId
                bookingType: $bookingType
                memberId: $memberId
              ) {
                edges {
                  durationMins
                  allowedPlayerCounts
                  node {
                    availability
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
